import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { makeStyles } from '@material-ui/core';
import PropTypes from 'prop-types';
import CKEditor from 'ckeditor4-react';
import Paper from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Paper';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import useCollection from 'Hooks/useCollection';
import featureEnabled, { pickImplementation } from 'Helpers/Affordances';
import fr from 'facet-rest';
import useUserContext from 'Context/UserContext';
import {
  createActionItem,
  getActionItemCollection,
} from 'Components/UI/ContextMenu/utils';

const OFF = 2;
CKEditor.editorUrl = '/ckeditor/ckeditor.js';

const config = ({ disableTextColors, height, customConfig }) => ({
  toolbarGroups: [
    { name: 'document', groups: ['mode', 'document', 'doctools'] },
    { name: 'forms' },
    {
      name: 'basicstyles',
      groups: disableTextColors
        ? ['basicstyles', 'cleanup']
        : ['basicstyles', 'colors', 'cleanup'],
    },
    {
      name: 'paragraph',
      groups: ['list', 'indent', 'blocks', 'align', 'bidi'],
    },
    { name: 'links' },
    { name: 'styles' },
    { name: 'tools' },
    { name: 'insert' },
  ],
  pasteFromWordRemoveFontStyles: false,
  pasteFromWordRemoveStyles: false,
  allowedContent: true,
  // The default plugins included in the basic setup define some buttons that
  // are not needed in a basic editor. They are removed here.
  removeButtons:
    'Cut,Copy,Paste,Undo,Redo,Anchor,Subscript,Superscript,Unlink,Source,Maximize,Table,Image,SpecialChar,HorizontalRule',
  // Dialog windows are also simplified.
  removeDialogTabs: 'link:advanced',
  removePlugins: 'elementspath,scayt,liststyle,tabletools,tableselection',
  height: height || '100%',
  width: '100%',
  disableNativeSpellChecker: false,
  toolbarCanCollapse: false,
  resize_enabled: false,
  contentsCss: '/ckeditor.css',
  extraPlugins: disableTextColors ? 'emoji' : 'colorbutton,emoji',
  colorButton_enableAutomatic: false,
  colorButton_historyRowLimit: 0,
  ...customConfig,
});

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    display: 'flex',
    '& .cke_top': {
      borderTopLeftRadius: theme.radius.xs,
      borderTopRightRadius: theme.radius.xs,
      display: 'flex',
      overflow: 'unset',
    },
    '& .cke_inner': {
      display: 'flex',
      flexDirection: 'column',
      borderRadius: theme.radius.xs,
      height: '100%',
    },
    '& .cke_contents': {
      borderBottomLeftRadius: theme.radius.xs,
      borderBottomRightRadius: theme.radius.xs,
      maxWidth: '100%',
      fontFamily: theme.typography.fontFamily,
      padding: 0,
      marginBottom: theme.spacing(1),
      display: 'flex',
    },
    '& .cke_ltr': {
      border: 0,
    },
  },
  rootSizeDefault: {},
  rootSizeSm: {
    height: theme.dimensions.sm,
  },
  rootSizeMd: {
    height: theme.dimensions.md,
  },
  rootSizeLg: {
    height: theme.dimensions.lg,
  },
  rootSizeFull: {
    height: '100%',
  },
  editor: {
    display: 'flex',
  },
}));

const RichTextEditor = React.forwardRef(({
  className,
  onChange,
  size,
  value,
  disableTextColors,
  height,
  agentOnly,
  onBeforeLoadProp,
  customConfig,
  test,
  dataTestAttributes,
  ...rest
}, ref) => {
  const classes = useStyles();
  const { agentResource } = useUserContext();
  const { householdResource } = useSimpleHousehold();
  const { collection: templateDictionaryCollection, setCollection: setTemplateDictionaryCollection } = useCollection();
  const { collection: actionItemCollection, setCollection: setActionItemCollection } = useCollection();
  const [nextScheduledMeetingResource] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const {
    rootNodeProps,
    logEvent,
  } = useEventTrackingLogEvent();

  async function getTemplateDictionaryCollection() {
    const coll = await agentResource?.relationship(fr.relationships.templateDictionary.name)?.link('related').get();

    if (coll) {
      setTemplateDictionaryCollection(coll);
    }
  }

  useEffect(() => {
    async function fetchData() {
      setActionItemCollection(await getActionItemCollection(householdResource));
    }
    fetchData();
  }, [householdResource?.id()]);

  useEffect(() => {
    if (agentResource) {
      getTemplateDictionaryCollection();
    }
  }, [agentResource?.id()]);

  const handleChange = (e) => {
    onChange(e.editor.getData());
  };

  function getAssociatePlannerTaskSubMenusAndItems() {
    if (!actionItemCollection) {
      return {};
    }
    const subMenus = actionItemCollection
      .meta()
      .field('assignedTo')
      .options()
      .list()
      .filter(v => v.attr('group') === 'AssociatePlanner')
      .map((option) => {
        return {
          label: option.attr('prompt'),
          group: 'customGroup',
          command: `create_planner_task_${option.attr('id')}`,
        };
      });

    const subMenuItems = {};
    subMenus.forEach((m) => { subMenuItems[m.label] = OFF; });
    return {
      subMenus,
      subMenuItems,
    };
  }

  function getInsertTemplateTokenSubMenusAndItemsMailMerge() {
    if (!templateDictionaryCollection) {
      return {};
    }
    const subMenus = templateDictionaryCollection.resources()
      .head()
      .attr('tokens')
      .resources()
      .list()
      .map((resource) => {
        return {
          label: resource.attr('label'),
          group: 'customGroup',
          command: `insert_template_token_${resource.attr('token')}`,
        };
      });
    const subMenuItems = {};
    subMenus.forEach((m) => { subMenuItems[m.label] = OFF; });
    return {
      subMenus,
      subMenuItems,
    };
  }

  const getInsertMailMergeSubMenusAndItems = pickImplementation(agentResource, {
    [fr.relationships.templateDictionary.name]: getInsertTemplateTokenSubMenusAndItemsMailMerge,
    [fr.relationships.default.name]: () => {
      return {
        subMenus: [],
        subMenuItems: {},
      };
    },
  });

  function configureContextMenu() {
    const { subMenus: associatePlannerTaskSubMenus, subMenuItems: associatePlannerTaskSubMenuItems } = getAssociatePlannerTaskSubMenusAndItems();
    const { subMenus: insertTemplateTokenSubMenus, subMenuItems: insertTemplateTokenSubMenuItems } = getInsertMailMergeSubMenusAndItems();

    const menus = {};

    if (actionItemCollection) {
      menus.plannerActionTask = {
        label: 'Create Planner Task',
        group: 'customGroup',
        command: 'create_planner_task',
      };
      menus.createAssociatePlannerActionTask = {
        label: 'Create Associate Planner Task',
        group: 'customGroup',
        getItems() {
          return associatePlannerTaskSubMenuItems;
        },
      };
    }

    if (agentResource?.relationship(fr.relationships.templateDictionary.name)) {
      menus.insertTemplateToken = {
        label: 'Insert Template Token',
        group: 'customGroup',
        getItems() {
          return insertTemplateTokenSubMenuItems;
        },
      };
    }

    if (associatePlannerTaskSubMenus) {
      associatePlannerTaskSubMenus.forEach((m) => { menus[m.label] = m; });
    }
    if (insertTemplateTokenSubMenus) {
      insertTemplateTokenSubMenus.forEach((m) => {
        menus[m.label] = m;
      });
    }
    setInitialized(true);
    return menus;
  }

  function createCommands(e) {
    e.addCommand('create_client_task', {
      exec(evt) {
        logEvent('Rich Text Right Click Menu: Creating client task');
        createActionItem(evt?.getSelection()?.getSelectedText(), true, null, householdResource, actionItemCollection, nextScheduledMeetingResource);
      },
    });
    e.addCommand('create_planner_task', {
      exec(evt) {
        logEvent('Rich Text Right Click Menu: Creating planner task');
        createActionItem(evt?.getSelection()?.getSelectedText(), false, null, householdResource, actionItemCollection, nextScheduledMeetingResource);
      },
    });

    if (actionItemCollection) {
      actionItemCollection
        .meta()
        .field('assignedTo')
        .options()
        .list()
        .forEach((opt) => {
          e.addCommand(`create_planner_task_${opt.attr('id')}`, {
            exec(evt) {
              logEvent('Rich Text Right Click Menu: Creating associate planner task');
              createActionItem(evt?.getSelection()?.getSelectedText(), false, opt.attr('id'), householdResource, actionItemCollection, nextScheduledMeetingResource);
            },
          });
        });
    }

    if (templateDictionaryCollection) {
      templateDictionaryCollection.resources()
        .head()
        .attr('tokens')
        .resources()
        .list()
        .forEach((resource) => {
          e.addCommand(`insert_template_token_${resource.attr('token')}`, {
            exec(evt) {
              logEvent('Rich Text Right Click Menu: inserting template token');
              evt.insertHtml(`<span class="token">${resource.attr('token')}</span><span> </span>`);
              const selection = evt?.editor?.getSelection();
              if (!selection) {
                return;
              }
              selection.getStartElement().removeClass('token');
            },
          });
        });
    }
  }

  const hasTemplateDictionaryFeature = featureEnabled(agentResource, fr.relationships.templateDictionary.name);

  if (!agentOnly) {
    if (!actionItemCollection || !agentResource || (hasTemplateDictionaryFeature && !templateDictionaryCollection)) {
      return null;
    }
  } else if (!agentResource || (hasTemplateDictionaryFeature && !templateDictionaryCollection)) {
    return null;
  }

  return (
    <Paper
      className={classNames(
        classes.root,
        {
          [classes.rootSizeDefault]: size === 'default',
          [classes.rootSizeSm]: size === 'sm',
          [classes.rootSizeMd]: size === 'md',
          [classes.rootSizeLg]: size === 'lg',
          [classes.rootSizeFull]: size === 'full',
        },
        className,
      )}
      variant="outlined"
      test={test}
      {...dataTestAttributes}
      {...rootNodeProps}
    >
      <CKEditor
        ref={ref}
        className={classes.editor}
        onChange={handleChange}
        data={value}
        config={config({ disableTextColors, height, customConfig })}
        onBeforeLoad={(editor) => {
          editor.on('instanceReady', (ckEditor) => {
            if (!initialized) {
              const e = ckEditor.editor;
              createCommands(e);
              if (e.contextMenu) {
                e.contextMenu.addListener(() => {
                  e.contextMenu.removeAll();
                  return {
                    clientActionTask: OFF,
                    plannerActionTask: OFF,
                    createAssociatePlannerActionTask: OFF,
                    insertTemplateToken: OFF,
                  };
                });
                e.addMenuGroup('customGroup', 500);
                e.addMenuItems(configureContextMenu());
              }
            }
            onBeforeLoadProp();
          });
        }}
        {...rest}
      />
    </Paper>
  );
});

RichTextEditor.defaultProps = {
  className: '',
  value: null,
  size: 'md',
  test: 'rich-text-editor-context-menu',
  disableTextColors: false,
  agentOnly: false,
  onBeforeLoadProp: () => {},
};

RichTextEditor.propTypes = {
  test: PropTypes.string,
  className: PropTypes.string,
  size: PropTypes.oneOf(['default', 'sm', 'md', 'lg']),
  onChange: PropTypes.func.isRequired,
  value: PropTypes.string,
  disableTextColors: PropTypes.bool,
  agentOnly: PropTypes.bool,
  onBeforeLoadProp: PropTypes.func,
};

export default RichTextEditor;
